import { AchievementRecords } from './AchievementRecords';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Award, Users, Trophy } from 'lucide-react';
import { api, json } from '../../api';
import { UnifiedSelect } from '../../components/UnifiedSelect';
import { Modal } from '../../components/Modal';
import { AdminHeading } from './shared';
import { Medal } from '../Achievements';
import './achievement-editor.css';
type Definition = {
  status: string;
  unlockedCount?: number;
  id: string;
  name: string;
  description: string;
  target: number;
  points: number;
  icon: number;
  hidden: boolean;
  badge: string;
  title: string;
};
const empty: Definition = {
  status: 'draft',
  id: '',
  name: '',
  description: '',
  target: 1,
  points: 10,
  icon: 0,
  hidden: false,
  badge: '',
  title: '',
};
export function AdminAchievements() {
  const [params, setParams] = useSearchParams();
  const tab = params.has('user') || params.get('tab') === 'players' ? 'players' : 'config';
  const [games, setGames] = useState<{ id: string; name: string }[]>([]),
    [game, setGame] = useState('@platform'),
    [items, setItems] = useState<Definition[]>([]),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [loaded, setLoaded] = useState(false),
    [edit, setEdit] = useState<{ index: number; value: Definition } | null>(null),
    [query, setQuery] = useState('');
  useEffect(() => {
    api<typeof games>('/admin/games')
      .then(setGames)
      .catch((e) => setMessage(e.message));
  }, []);
  useEffect(() => {
    let active = true;
    setLoaded(false);
    setItems([]);
    setMessage('');
    api<{ items: Definition[] }>('/admin/achievements/' + encodeURIComponent(game))
      .then((r) => {
        if (active) {
          setItems(r.items);
          setLoaded(true);
        }
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [game]);
  const field = (key: keyof Definition, value: string | number | boolean) =>
    setEdit((e) => (e ? { ...e, value: { ...e.value, [key]: value } } : null));
  async function save() {
    if (!edit) return;
    setBusy(true);
    setMessage('');
    const next =
      edit.index < 0
        ? [...items, edit.value]
        : items.map((i, n) => (n === edit.index ? edit.value : i));
    try {
      await api('/admin/achievements/' + encodeURIComponent(game), json('PUT', { items: next }));
      setItems(next);
      setEdit(null);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="achievement-editor">
      <AdminHeading kicker="MOYU ISLAND / ACHIEVEMENTS" title="成就管理" />
      <nav className="achievement-tabs" aria-label="成就管理分区">
        {(
          [
            ['config', '成就配置', Award],
            ['players', '玩家记录', Users],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={String(key)}
            className={tab === key ? 'active' : ''}
            onClick={() => setParams(key === 'players' ? { tab: 'players' } : {})}
          >
            <Icon size={18} />
            {String(label)}
          </button>
        ))}
      </nav>
      {tab === 'players' ? (
        <AchievementRecords userId={params.get('user') || undefined} />
      ) : (
        <>
          <div className="achievement-summary">
            {(
              [
                ['成就数量', items.length, '/ 50', Award],
                ['已启用', items.filter((i) => i.status === 'active').length, '项', Trophy],
                ['配置积分', items.reduce((n, i) => n + i.points, 0), '/ 1,000', Plus],
              ] as const
            ).map(([label, value, unit, Icon]) => (
              <div key={String(label)}>
                <span className="achievement-summary-icon">
                  <Icon size={22} />
                </span>
                <div>
                  <small>{String(label)}</small>
                  <strong>
                    {loaded ? String(value) : '—'} <em>{String(unit)}</em>
                  </strong>
                </div>
              </div>
            ))}
          </div>
          <section className="achievement-panel">
            <div className="achievement-panel-tools">
              <UnifiedSelect
                aria-label="选择游戏"
                value={game}
                onChange={(e) => setGame(e.target.value)}
              >
                <option value="@platform">平台成就</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </UnifiedSelect>
              <input
                aria-label="搜索成就"
                placeholder="搜索成就名称或 ID"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                className="button"
                disabled={!loaded || items.length >= 50}
                onClick={() => {
                  setMessage('');
                  setEdit({ index: -1, value: { ...empty } });
                }}
              >
                <Plus size={16} />
                添加成就
              </button>
            </div>
            {message && !edit && (
              <p className="ach-error" role="alert">
                {message}
              </p>
            )}
            <div className="achievement-config-head">
              <span>成就 / 解锁条件</span>
              <span>状态</span>
              <span>积分与荣誉</span>
              <span>已解锁</span>
              <span />
            </div>
            {items
              .map((d, n) => ({ d, n }))
              .filter(({ d }) => (d.name + ' ' + d.id).includes(query))
              .map(({ d, n }) => (
                <article className="achievement-config-row" key={d.id}>
                  <div className="achievement-config-name">
                    <Medal icon={d.icon} />
                    <div>
                      <strong>{d.name}</strong>
                      <small>{d.description || d.id}</small>
                    </div>
                  </div>
                  <span className={'achievement-status ' + d.status}>
                    {d.status === 'active' ? '已启用' : d.status === 'disabled' ? '已停用' : '草稿'}
                    {d.hidden ? ' · 隐藏' : ''}
                  </span>
                  <div>
                    <b>
                      {d.points} <small>积分</small>
                    </b>
                    <small>{[d.badge, d.title].filter(Boolean).join(' · ') || '无附加荣誉'}</small>
                  </div>
                  <span>
                    {d.unlockedCount || 0} <small>人</small>
                  </span>
                  <button
                    className="button secondary small"
                    onClick={() => {
                      setMessage('');
                      setEdit({ index: n, value: { ...d } });
                    }}
                  >
                    <Pencil size={14} />
                    编辑
                  </button>
                </article>
              ))}
            {!loaded ? (
              <div className="achievement-empty">正在加载成就…</div>
            ) : (
              !items.some((d) => (d.name + ' ' + d.id).includes(query)) && (
                <div className="achievement-empty">
                  <Award size={36} />
                  <strong>{query ? '没有匹配的成就' : '尚未配置成就'}</strong>
                </div>
              )
            )}
          </section>
        </>
      )}
      {edit && (
        <Modal
          title={edit.index < 0 ? '添加成就' : '编辑成就'}
          onClose={() => {
            setEdit(null);
            setMessage('');
          }}
          dismissible={!busy}
          className="achievement-config-modal"
        >
          <form
            className="achievement-editor-fields"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div className="achievement-icon-picker" aria-label="成就图标">
              {Array.from({ length: 9 }, (_, icon) => (
                <button
                  type="button"
                  aria-label={'图标 ' + (icon + 1)}
                  aria-pressed={edit.value.icon === icon}
                  className={edit.value.icon === icon ? 'selected' : ''}
                  key={icon}
                  onClick={() => field('icon', icon)}
                >
                  <Medal icon={icon} />
                </button>
              ))}
            </div>
            <div className="achievement-field-pair">
              <label>
                成就名称
                <input
                  required
                  maxLength={40}
                  value={edit.value.name}
                  onChange={(e) => field('name', e.target.value)}
                />
              </label>
              <label>
                成就 ID
                <input
                  required
                  disabled={edit.index >= 0}
                  maxLength={64}
                  value={edit.value.id}
                  onChange={(e) => field('id', e.target.value)}
                />
              </label>
            </div>
            <label>
              解锁条件说明
              <input
                required
                maxLength={200}
                value={edit.value.description}
                onChange={(e) => field('description', e.target.value)}
              />
            </label>
            <div className="achievement-field-pair">
              {(['target', 'points'] as const).map((k) => (
                <label key={k}>
                  {k === 'target' ? '目标次数' : '成就积分'}
                  <input
                    required
                    type="number"
                    min={k === 'target' ? 1 : 0}
                    max={k === 'target' ? 1000000 : 100}
                    value={edit.value[k]}
                    onChange={(e) => field(k, Number(e.target.value))}
                  />
                </label>
              ))}
            </div>
            <div className="achievement-field-pair">
              {(['badge', 'title'] as const).map((k) => (
                <label key={k}>
                  {k === 'badge' ? '奖励徽章' : '奖励称号'}
                  <input
                    placeholder="可选"
                    maxLength={30}
                    value={edit.value[k]}
                    onChange={(e) => field(k, e.target.value)}
                  />
                </label>
              ))}
            </div>
            <label>
              发布状态
              <UnifiedSelect
                value={edit.value.status}
                onChange={(e) => field('status', e.target.value)}
              >
                <option value="draft">草稿</option>
                <option value="active">已启用</option>
                <option value="disabled">已停用</option>
              </UnifiedSelect>
            </label>
            <label className="achievement-checkbox">
              <input
                type="checkbox"
                checked={edit.value.hidden}
                onChange={(e) => field('hidden', e.target.checked)}
              />
              解锁前隐藏名称与条件
            </label>
            {message && (
              <p className="ach-error" role="alert">
                {message}
              </p>
            )}
            <footer className="achievement-dialog-actions">
              <button
                type="button"
                className="button secondary"
                disabled={busy}
                onClick={() => setEdit(null)}
              >
                取消
              </button>
              <button className="button" disabled={busy}>
                {busy ? '保存中…' : '保存成就'}
              </button>
            </footer>
          </form>
        </Modal>
      )}
    </div>
  );
}
